import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useSocket } from '../../contexts/SocketContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const OverlayControl = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { socket, connected, joinOverlay, emitOverlayUpdate } = useSocket()

  const [overlay, setOverlay] = useState(null)
  const [events, setEvents] = useState([])
  const [popupElements, setPopupElements] = useState([])
  const [selectedPopups, setSelectedPopups] = useState([])
  const [availableChats, setAvailableChats] = useState([])
  const [comments, setComments] = useState([])
  const [headlines, setHeadlines] = useState({
    headline1: '',
    subHeadline: '',
    headline3: ''
  })
  const [updateType, setUpdateType] = useState(null)
  const [controlState, setControlState] = useState({
    countdownState: 'AWAITING_UPDATE',
    primaryCountdownVisible: false,
    launchWindowVisible: false,
    showDays: false,
    showHours: true,
    primaryStatusVisible: false,
    secondaryStatusVisible: false,
    streamDelay: 0,
    secondaryClockClose: false,
    commentsVisible: true,
    statusVisible: true,
    eventsViewType: 'vertical', // 'vertical' or 'horizontal'
    headerVisible: true,
    subheaderVisible: true,
    header3Visible: true,
    updateBannerVisible: false,
    chatConnected: false,
    connectedChatId: null,
    memberScrollVisible: true,
    memberBarVisible: true,
    emergencyMode: false
  })
  const [showEmergencyModal, setShowEmergencyModal] = useState(false)
  const [emergencyPassword, setEmergencyPassword] = useState('')
  const [pendingChanges, setPendingChanges] = useState({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  // Removed activeTab since we now have a single event list
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchOverlay()
    fetchAvailableChats()
    joinOverlay(id)
  }, [id])

  // Fetch comments when chat is connected
  useEffect(() => {
    if (controlState.connectedChatId) {
      fetchComments()
      // Set up interval to refresh comments every 5 seconds
      const interval = setInterval(() => {
        fetchComments()
      }, 5000)
      return () => clearInterval(interval)
    } else {
      setComments([])
    }
  }, [controlState.connectedChatId])

  // Listen for new comments via WebSocket
  useEffect(() => {
    if (!socket || !connected) return

    const handleCommentAdded = (data) => {
      console.log('🔧 New comment received:', data)
      if (data.chatConfigId === controlState.connectedChatId) {
        // Refresh comments when new comment is added
        fetchComments()
      }
    }

    socket.on('comment-added', handleCommentAdded)

    return () => {
      socket.off('comment-added', handleCommentAdded)
    }
  }, [socket, connected, controlState.connectedChatId])

  // Listen for real-time WebSocket updates
  useEffect(() => {
    if (!socket || !connected) {
      console.log('🔧 OverlayControl - Socket not connected, skipping listener setup')
      return
    }

    console.log('🔧 OverlayControl - Setting up WebSocket listeners for overlay:', id)

    const handleOverlayUpdate = (updateData) => {
      console.log('🔧 OverlayControl - Received WebSocket update:', updateData)

      try {
        if (updateData.type === 'control-update') {
          // Update control state from server
          setControlState(prev => ({
            ...prev,
            ...updateData.data
          }))
          // Clear pending changes if they match
          setPendingChanges(prev => {
            const newPending = { ...prev }
            Object.keys(updateData.data).forEach(key => {
              if (newPending[key] === updateData.data[key]) {
                delete newPending[key]
              }
            })
            const hasChanges = Object.keys(newPending).length > 0
            setHasUnsavedChanges(hasChanges)
            return newPending
          })
          console.log('🔧 Control state updated from WebSocket:', updateData.data)
        } else if (updateData.type === 'control-state-update') {
          // Update control state (countdown state changes)
          setControlState(prev => ({
            ...prev,
            ...updateData.data
          }))
          console.log('🔧 Control state updated from WebSocket:', updateData.data)
        } else if (updateData.type === 'event-update') {
          // Update event
          setEvents(prev => prev.map(event =>
            event.id === updateData.eventId ? { ...event, ...updateData.data } : event
          ))
          console.log('🔧 Event updated from WebSocket:', updateData.eventId, updateData.data)
        } else if (updateData.type === 'popup-update') {
          // Update active popups
          setSelectedPopups(updateData.data.activePopups || [])
          console.log('🔧 Active popups updated from WebSocket:', updateData.data.activePopups)
        } else if (updateData.type === 'overlay-update') {
          // Update overlay data (title, headline, etc.)
          setOverlay(prev => prev ? { ...prev, ...updateData.data } : null)
          // Update local state if headline or updateType changed
          if (updateData.data.headline1 !== undefined || updateData.data.subHeadline !== undefined || updateData.data.headline3 !== undefined || updateData.data.updateType !== undefined) {
            setHeadlines(prev => ({
              ...prev,
              headline1: updateData.data.headline1 !== undefined ? updateData.data.headline1 : prev.headline1,
              subHeadline: updateData.data.subHeadline !== undefined ? updateData.data.subHeadline : prev.subHeadline,
              headline3: updateData.data.headline3 !== undefined ? updateData.data.headline3 : prev.headline3
            }))
            if (updateData.data.updateType !== undefined) {
              setUpdateType(updateData.data.updateType)
            }
          }
          console.log('🔧 Overlay data updated from WebSocket:', updateData.data)
        } else if (updateData.type === 'popup-selection-update') {
          // Update popup selection - refresh to get updated popup list
          console.log('🔧 Popup selection updated from WebSocket:', updateData.data.activePopupIds)
          // Refresh popup data
          const refreshPopups = async () => {
            try {
              const activePopupsResponse = await api.get(`/overlays/${id}/active-popups`)
              setSelectedPopups(activePopupsResponse.data.activePopups || [])
            } catch (error) {
              console.error('Failed to refresh popups:', error)
            }
          }
          refreshPopups()
        }
      } catch (error) {
        console.error('Error handling WebSocket update:', error)
      }
    }

    socket.on('overlay-updated', handleOverlayUpdate)

    const handleChatUpdate = (updateData) => {
      console.log('🔧 OverlayControl - Received Chat WebSocket update:', updateData)
      if (updateData.type === 'comment-update') {
        fetchComments()
      } else if (updateData.type === 'all-comments-hidden') {
        setComments(prev => prev.map(c => ({ ...c, isVisible: false })))
      }
    }

    socket.on('chat-updated', handleChatUpdate)

    return () => {
      console.log('🔧 OverlayControl - Cleaning up WebSocket listeners')
      socket.off('overlay-updated', handleOverlayUpdate)
      socket.off('chat-updated', handleChatUpdate)
    }
  }, [socket, connected, id])

  // Warn user about unsaved changes when navigating away
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const fetchOverlay = async () => {
    try {
      console.log('Fetching overlay:', id)
      const response = await api.get(`/overlays/${id}`)
      console.log('Overlay response:', response.data)

      const { overlay: overlayData, events: eventsData, controlState: controlData } = response.data

      console.log('Server controlData:', controlData)

      setOverlay(overlayData)
      setEvents(eventsData || [])
      setHeadlines({
        headline1: overlayData.headline1 || '',
        subHeadline: overlayData.subHeadline || '',
        headline3: overlayData.headline3 || ''
      })
      setUpdateType(overlayData.updateType || null)
      if (controlData) {
        // Merge server data with local defaults to ensure all fields are present
        setControlState(prev => ({
          ...prev,
          ...controlData,
          // Ensure these fields have proper defaults if not in server response
          chatConnected: controlData.chatConnected !== undefined ? controlData.chatConnected : false,
          connectedChatId: controlData.connectedChatId || null
        }))
      } else {
        // Initialize control state with overlay's statusVisible setting
        setControlState(prev => ({
          ...prev,
          statusVisible: overlayData.statusVisible !== false
        }))
      }

      // Fetch popup elements for controls
      const popupResponse = await api.get(`/overlays/${id}/popups`)
      setPopupElements(popupResponse.data.popups || [])

      // Fetch active popups
      const activePopupsResponse = await api.get(`/overlays/${id}/active-popups`)
      setSelectedPopups(activePopupsResponse.data.activePopups || [])
    } catch (error) {
      console.error('Failed to fetch overlay:', error)
      toast.error('Failed to load overlay')
      navigate('/overlay')
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableChats = async () => {
    try {
      const response = await api.get('/chat')
      const chats = response.data.chatConfigs || []
      console.log(`Loaded ${chats.length} chat configurations`)
      setAvailableChats(chats)
    } catch (error) {
      console.error('Failed to fetch chat configurations:', error)
    }
  }

  const fetchComments = async () => {
    if (!controlState.connectedChatId) return

    try {
      const response = await api.get(`/chat/${controlState.connectedChatId}/comments`, {
        params: {
          limit: 100,
          offset: 0
        }
      })
      setComments(response.data.comments || [])
    } catch (error) {
      console.error('Failed to fetch comments:', error)
    }
  }

  const toggleCommentVisibility = async (commentId, isVisible) => {
    if (!controlState.connectedChatId) return

    try {
      // Update local state immediately for instant visual feedback
      setComments(prev => prev.map(comment => {
        if (comment.id === commentId) {
          return { ...comment, isVisible: isVisible }
        } else if (isVisible) {
          // If making a comment visible, hide all other comments locally
          return { ...comment, isVisible: false }
        }
        return comment
      }))

      // If making a comment visible, deselect all popups locally
      if (isVisible) {
        setSelectedPopups([])
        // Also update local popup elements state
        setPopupElements(prev => prev.map(el => ({ ...el, is_active: false })))
      }

      // Update on server
      await api.put(`/chat/${controlState.connectedChatId}/comments/${commentId}`, {
        isVisible: isVisible
      })

      toast.success(`Comment ${isVisible ? 'shown' : 'hidden'} on broadcast`)
    } catch (error) {
      console.error('Failed to toggle comment visibility:', error)
      toast.error('Failed to update comment visibility')
      // Revert local state on error
      setComments(prev => prev.map(comment =>
        comment.id === commentId ? { ...comment, isVisible: !isVisible } : comment
      ))
    }
  }

  const updateControlState = (updates) => {
    console.log('Updating control state locally:', updates)
    const newState = { ...controlState, ...updates }
    console.log('New control state:', newState)
    setControlState(newState)

    // Track pending changes instead of saving immediately
    setPendingChanges(prev => ({ ...prev, ...updates }))
    setHasUnsavedChanges(true)
  }

  const saveControlState = async () => {
    if (!hasUnsavedChanges) return

    try {
      // Save control state changes if any
      if (hasUnsavedChanges && Object.keys(pendingChanges).length > 0) {
        console.log('Saving control state:', pendingChanges)
        const response = await api.put(`/overlays/${id}/control`, pendingChanges)
        console.log('Control state save response:', response.data)
        console.log('Chat connection settings saved:', {
          chatConnected: pendingChanges.chatConnected,
          connectedChatId: pendingChanges.connectedChatId
        })

        emitOverlayUpdate(id, {
          type: 'control-update',
          data: pendingChanges
        })

        setPendingChanges({})
        setHasUnsavedChanges(false)

        toast.success('Control settings saved successfully')

        // Special message for chat connection
        if (pendingChanges.chatConnected !== undefined || pendingChanges.connectedChatId !== undefined) {
          toast.success('Chat connection settings updated!')
        }
      }
    } catch (error) {
      console.error('Failed to save changes:', error)
      toast.error('Failed to save changes')
    }
  }

  const handleCountdownStateChange = async (newState) => {
    try {
      const response = await api.put(`/overlays/${id}/countdown-state`, {
        countdownState: newState
      })

      // Only update countdown state, don't modify primary countdown visibility
      const updates = { countdownState: newState }

      setControlState(prev => ({
        ...prev,
        ...updates
      }))

      // Emit the overlay update to broadcast
      emitOverlayUpdate(id, {
        type: 'control-state-update',
        data: updates
      })

      toast.success(`Countdown state changed to ${newState}`)
    } catch (error) {
      console.error('Error updating countdown state:', error)
      toast.error('Failed to update countdown state')
    }
  }

  const updateEvent = async (eventId, updates) => {
    try {
      console.log('Updating event:', eventId, updates)

      // First, ensure the event completion record exists
      const event = events.find(e => e.id === eventId)
      if (!event) {
        toast.error('Event not found')
        return
      }

      console.log('Current event state before update:', event)

      const response = await api.put(`/overlays/${id}/events/${eventId}`, updates)
      console.log('Event update response:', response.data)

      // Update local state immediately for better UX
      setEvents(prev => prev.map(event =>
        event.id === eventId ? { ...event, ...updates } : event
      ))

      emitOverlayUpdate(id, {
        type: 'event-update',
        eventId,
        data: updates
      })

      console.log('Event updated successfully:', eventId, updates)
      toast.success('Event updated successfully')
    } catch (error) {
      console.error('Failed to update event:', error)
      toast.error('Failed to update event')
    }
  }


  const handleToggle = (field) => {
    console.log(`Toggling ${field}: ${controlState[field]} -> ${!controlState[field]}`)
    updateControlState({ [field]: !controlState[field] })
  }

  const handleHeadlineChange = (field, value) => {
    setHeadlines(prev => ({ ...prev, [field]: value }))
  }

  const saveHeadlines = async () => {
    try {
      const response = await api.put(`/overlays/${id}`, {
        headline1: headlines.headline1,
        subHeadline: headlines.subHeadline,
        headline3: headlines.headline3,
        updateType: updateType
      })
      
      // Update local overlay state
      setOverlay(prev => ({
        ...prev,
        headline1: headlines.headline1,
        subHeadline: headlines.subHeadline,
        headline3: headlines.headline3,
        updateType: updateType
      }))

      // Emit WebSocket update to refresh broadcast output
      emitOverlayUpdate(id, {
        type: 'overlay-update'
      })

      toast.success('Headlines updated successfully')
    } catch (error) {
      console.error('Failed to save headlines:', error)
      toast.error('Failed to save headlines')
    }
  }

  const handleEmergencyActivation = async () => {
    if (!emergencyPassword || emergencyPassword.trim() === '') {
      toast.error('Please enter your password')
      return
    }

    try {
      // Verify password against user's account
      const response = await api.post('/auth/verify-password', {
        password: emergencyPassword
      })

      if (response.data.valid) {
        console.log('✅ Password verified, activating emergency mode')

        // Update local state immediately
        const newState = { ...controlState, emergencyMode: true }
        setControlState(newState)

        // Save immediately to server (don't wait for manual save)
        await api.put(`/overlays/${id}/control`, { emergencyMode: true })

        // Clear pending changes for emergency mode since we saved it
        setPendingChanges(prev => {
          const updated = { ...prev }
          delete updated.emergencyMode
          return updated
        })

        setShowEmergencyModal(false)
        setEmergencyPassword('')
        toast.success('Emergency mode activated', { icon: '🚨' })

        // Emit socket update for real-time sync
        emitOverlayUpdate(id, {
          type: 'control-update',
          data: { emergencyMode: true }
        })
      } else {
        toast.error('Incorrect password')
        setEmergencyPassword('')
      }
    } catch (error) {
      console.error('❌ Password verification failed:', error)
      if (error.response?.status === 401) {
        toast.error('Incorrect password')
      } else {
        toast.error('Failed to verify password')
      }
      setEmergencyPassword('')
    }
  }

  const handleEventToggle = (eventId, field) => {
    const event = events.find(e => e.id === eventId)
    if (event) {
      // Ensure we have a boolean value, defaulting to false if undefined/null
      const currentValue = Boolean(event[field])
      const newValue = !currentValue
      console.log(`Toggling ${field} for event ${eventId}: ${currentValue} -> ${newValue}`)

      // Send all three fields to maintain their current values
      updateEvent(eventId, {
        isComplete: Boolean(event.isComplete),
        isVisible: Boolean(event.isVisible),
        hasClock: Boolean(event.hasClock),
        [field]: newValue
      })
    }
  }

  const handleBulkEventToggle = async (field, value) => {
    try {
      console.log(`Bulk toggling ${field} to ${value} for all events`)

      // Update all events in parallel
      const updatePromises = events.map(event =>
        updateEvent(event.id, {
          isComplete: Boolean(event.isComplete),
          isVisible: Boolean(event.isVisible),
          hasClock: Boolean(event.hasClock),
          [field]: value
        })
      )

      await Promise.all(updatePromises)

      toast.success(`All events ${field} set to ${value ? 'enabled' : 'disabled'}`)
    } catch (error) {
      console.error('Failed to bulk update events:', error)
      toast.error('Failed to update events')
    }
  }

  const triggerPopup = (elementType, elementName) => {
    emitOverlayUpdate(id, {
      type: 'popup-trigger',
      data: { elementType, elementName }
    })
    toast.success(`${elementName} popup triggered`)
  }

  const togglePopupSelection = async (popup) => {
    const isSelected = selectedPopups.some(selected => selected.id === popup.id)
    const previousSelectedPopup = selectedPopups.length > 0 ? selectedPopups[0] : null
    let newSelectedPopups = []
    let shouldBeActive = false

    if (isSelected) {
      // If clicking the same popup, deselect it (clear all)
      newSelectedPopups = []
      shouldBeActive = false
    } else {
      // If clicking a different popup, select only this one (radio button behavior)
      newSelectedPopups = [popup]
      shouldBeActive = true

      // If enabling a popup, hide all comments locally
      setComments(prev => prev.map(c => ({ ...c, isVisible: false })))
    }

    // Update local state immediately for better UX
    setSelectedPopups(newSelectedPopups)

    // Update popup element's is_active status
    try {
      // Set the clicked popup's active status
      await api.put(`/popups/${popup.id}`, { isActive: shouldBeActive })

      // If switching to a different popup, set the previous one to inactive
      if (!isSelected && previousSelectedPopup && previousSelectedPopup.id !== popup.id) {
        await api.put(`/popups/${previousSelectedPopup.id}`, { isActive: false })

        // Update local state for previous popup
        setPopupElements(prevElements =>
          prevElements.map(el =>
            el.id === previousSelectedPopup.id ? { ...el, is_active: false } : el
          )
        )
      }

      // Update local popup elements state for current popup
      setPopupElements(prevElements =>
        prevElements.map(el =>
          el.id === popup.id ? { ...el, is_active: shouldBeActive } : el
        )
      )
    } catch (error) {
      console.error('Failed to update popup active status:', error)
      toast.error('Failed to update popup status')
    }

    // Save immediately to server
    try {
      const activePopupIds = newSelectedPopups.length > 0 ? [newSelectedPopups[0].id] : []
      await api.put(`/overlays/${id}/active-popups`, { activePopupIds })
      console.log('Popup selection auto-saved:', activePopupIds)

      // Emit socket update for real-time sync
      emitOverlayUpdate(id, {
        type: 'popup-selection-update',
        data: { activePopupIds }
      })

      toast.success('Popup selection saved automatically')
    } catch (error) {
      console.error('Failed to save popup selection:', error)
      toast.error('Failed to save popup selection')
      // Revert local state on error
      setSelectedPopups(selectedPopups)
    }
  }


  // Helper function to get elements by type
  const getElementsByType = (type) => {
    return popupElements.filter(element => element.elementType === type)
  }

  // Toggle Switch Component
  const ToggleSwitch = ({ checked, onChange }) => (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div className={`w-12 h-6 rounded-full transition-colors ${checked ? 'bg-red-600' : 'bg-gray-700'}`}>
        <div className={`w-5 h-5 bg-white rounded-full border border-gray-300 transition-transform transform ${checked ? 'translate-x-[0.75rem]' : 'translate-x-0.5'} mt-0.5`}></div>
      </div>
    </label>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Loading overlay control...</p>
        </div>
      </div>
    )
  }

  if (!overlay) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl">Overlay not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <div className="px-6 pb-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => {
              if (hasUnsavedChanges) {
                if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                  navigate('/overlay')
                }
              } else {
                navigate('/overlay')
              }
            }}
            className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-avenir"
          >
            <span>&lt;</span>
            BACK
          </button>
        </div>

        {/* Title and Subtitle Section */}
        <div className="mb-8">
          <div className="flex items-center gap-8 mb-6">
            <div className="flex-1">
              <div className="text-white text-2xl font-bold mb-2">
                {overlay.title || 'TITLE'}
              </div>
              <div className="text-white text-lg">
                {overlay.subtitle || 'SUBTITLE'}
              </div>
              {hasUnsavedChanges && (
                <div className="text-yellow-400 text-sm mt-2">
                  ⚠️ You have unsaved control settings
                </div>
              )}
              {controlState.emergencyMode && (
                <div className="text-red-500 text-sm mt-2 font-bold">
                  🚨 EMERGENCY MODE ACTIVE
                </div>
              )}
            </div>
            {/* Emergency Mode Button */}
            <div>
              <button
                onClick={async () => {
                  if (controlState.emergencyMode) {
                    // Deactivate emergency mode
                    try {
                      const newState = { ...controlState, emergencyMode: false }
                      setControlState(newState)

                      // Save immediately to server
                      await api.put(`/overlays/${id}/control`, { emergencyMode: false })

                      // Clear pending changes
                      setPendingChanges(prev => {
                        const updated = { ...prev }
                        delete updated.emergencyMode
                        return updated
                      })

                      toast.success('Emergency mode deactivated')

                      // Emit socket update
                      emitOverlayUpdate(id, {
                        type: 'control-update',
                        data: { emergencyMode: false }
                      })
                    } catch (error) {
                      console.error('Failed to deactivate emergency mode:', error)
                      toast.error('Failed to deactivate emergency mode')
                      setControlState(prev => ({ ...prev, emergencyMode: true }))
                    }
                  } else {
                    // Show password prompt
                    setShowEmergencyModal(true)
                  }
                }}
                className={`px-6 py-2 text-sm font-bold transition-colors ${controlState.emergencyMode
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-red-900 text-white hover:bg-red-800'
                  }`}
              >
                {controlState.emergencyMode ? 'DEACTIVATE EMERGENCY MODE' : 'EMERGENCY MODE'}
              </button>
            </div>
            <div className="flex items-center gap-4 bg-[#1c1c1c] p-6">
              <div className="text-right">
                <div className="text-white text-sm">Stream</div>
                <div className="text-white text-sm">Delay</div>
              </div>
              <input
                type="number"
                value={controlState.streamDelay}
                onChange={(e) => updateControlState({ streamDelay: parseInt(e.target.value) || 0 })}
                className="bg-black border border-white text-white px-3 py-1 text-sm w-20 text-center"
                min="0"
              />

              <button
                onClick={saveControlState}
                disabled={!hasUnsavedChanges}
                className={`px-6 py-1 text-sm transition-colors ${hasUnsavedChanges
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
              >
                {hasUnsavedChanges ? 'SAVE' : 'SAVED'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-2 gap-8">
          {/* Left Side - Countdown Timeline */}
          <div className="space-y-8">
            {/* Countdown Timeline */}
            <div className='bg-[#1c1c1c] p-6'>
              <h2 className="text-white text-xl text-center font-bold mb-6">Countdown Timeline</h2>

              {/* Flight Events Header */}
              <div className="mb-6 text-center">
                <h3 className="text-white text-lg font-medium">Flight Events</h3>
              </div>

              {/* Bulk Controls */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkEventToggle('isVisible', true)}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Show All
                  </button>
                  <button
                    onClick={() => handleBulkEventToggle('isVisible', false)}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Hide All
                  </button>
                </div>
                <div className="flex gap-4 text-xs text-white items-center">
                  <div className="text-center min-w-[60px]">COMPLETE</div>
                  <div className="text-center min-w-[60px]">VISIBLE</div>
                  <div className="text-center min-w-[60px]">CLOCK</div>
                </div>
              </div>

              {/* Events List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {events.length > 0 ? (
                  events.map((event) => (
                    <div key={event.id} className="flex justify-between items-center py-1">
                      <div className="text-white text-sm truncate flex-1">{event.eventName}</div>
                      <div className="flex gap-4 items-center">
                        <div className="flex justify-center min-w-[60px]">
                          <input
                            type="checkbox"
                          checked={event.isComplete || false}
                          onChange={() => handleEventToggle(event.id, 'isComplete')}
                            className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500 focus:ring-2"
                        />
                        </div>
                        <div className="flex justify-center min-w-[60px]">
                          <input
                            type="checkbox"
                          checked={event.isVisible || false}
                          onChange={() => handleEventToggle(event.id, 'isVisible')}
                            className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500 focus:ring-2"
                        />
                        </div>
                        <div className="flex justify-center min-w-[60px]">
                          <input
                            type="checkbox"
                          checked={event.hasClock || false}
                          onChange={() => handleEventToggle(event.id, 'hasClock')}
                            className="w-4 h-4 text-red-600 bg-gray-700 border-gray-600 rounded focus:ring-red-500 focus:ring-2"
                        />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Show message when no events exist
                  <div className="flex justify-center items-center py-8">
                    <div className="text-white text-sm opacity-75">
                      No flight events configured
                    </div>
                  </div>
                )}
              </div>

              {/* Events View Type */}
              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-white">Events View</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateControlState({ eventsViewType: 'vertical' })}
                      className={`px-3 py-1 text-sm font-medium border border-white ${controlState.eventsViewType === 'vertical'
                          ? 'bg-red-600 text-white'
                          : 'bg-black text-white hover:bg-gray-800'
                        }`}
                    >
                      Vertical
                    </button>
                    <button
                      onClick={() => updateControlState({ eventsViewType: 'horizontal' })}
                      className={`px-3 py-1 text-sm font-medium border border-white ${controlState.eventsViewType === 'horizontal'
                          ? 'bg-red-600 text-white'
                          : 'bg-black text-white hover:bg-gray-800'
                        }`}
                    >
                      Horizontal
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat & Comments Section */}
            <div className='bg-[#1c1c1c] p-6'>
              <h2 className="text-white text-xl font-bold mb-6 text-center">Chat & Comments</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white">Comments</span>
                  <ToggleSwitch
                    checked={controlState.commentsVisible}
                    onChange={() => handleToggle('commentsVisible')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Connect Chat</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">({String(controlState.chatConnected)})</span>
                    <ToggleSwitch
                      checked={controlState.chatConnected}
                      onChange={() => handleToggle('chatConnected')}
                    />
                  </div>
                </div>

                {(controlState.chatConnected || true) && (
                  <div className="ml-4 mt-3 p-3 bg-gray-800 bg-opacity-50 rounded border border-gray-600">
                    <label className="block text-white text-sm mb-2 font-medium">Select Chat Configuration:</label>
                    <select
                      value={controlState.connectedChatId || ''}
                      onChange={(e) => updateControlState({ connectedChatId: e.target.value || null })}
                      className="w-full bg-black border border-gray-500 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    >
                      <option value="">Choose a chat configuration...</option>
                      {availableChats.length > 0 ? (
                        availableChats.map((chat) => (
                          <option key={chat.id} value={chat.id}>
                            {chat.name} {chat.isActive ? '(Active)' : '(Inactive)'}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No chat configurations available</option>
                      )}
                    </select>
                    {availableChats.length === 0 && (
                      <div className="mt-3 p-2 bg-yellow-900 bg-opacity-30 border border-yellow-600 rounded">
                        <p className="text-yellow-200 text-xs mb-1">
                          No chat configurations found.
                        </p>
                        <Link
                          to="/chat"
                          className="text-red-400 text-xs hover:text-red-300 underline font-medium"
                        >
                          Create a chat configuration first →
                        </Link>
                      </div>
                    )}
                    {controlState.connectedChatId && (
                      <div className="mt-2 p-2 bg-green-900 bg-opacity-30 border border-green-600 rounded">
                        <p className="text-green-200 text-xs">
                          ✓ Chat connected - Comments will display in broadcast
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Chat Comments List */}
                {controlState.connectedChatId && (
                  <div className="mt-4">
                    <div className="mb-4">
                      <h3 className="text-white text-lg font-medium">Chat Comments</h3>
                    </div>

                    <div className="space-y-2 max-h-96 overflow-y-auto bg-gray-900 bg-opacity-50 p-4 rounded">
                      {comments.length > 0 ? (
                        comments.map((comment) => (
                          <div
                            key={comment.id}
                            className={`bg-gray-800 bg-opacity-50 p-3 rounded border ${comment.isVisible ? 'border-green-600' : 'border-gray-700'
                              }`}
                          >
                            <div className="flex items-start gap-2 mb-2">
                              {comment.authorThumbnailUrl && (
                                <img
                                  src={comment.authorThumbnailUrl}
                                  alt={comment.authorName}
                                  className="w-6 h-6 rounded-full"
                                />
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-white text-sm font-medium">
                                    {comment.authorName}
                                  </span>
                                  {comment.amountMicros && comment.amountMicros > 0 && (
                                    <span className="text-yellow-400 text-xs font-medium">
                                      ${(comment.amountMicros / 1000000).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                                <p className="text-gray-300 text-sm break-words">
                                  {comment.commentText}
                                </p>
                                <div className="text-gray-500 text-xs mt-1">
                                  {new Date(comment.createdAt).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                              <span className={`text-xs font-medium ${comment.isVisible ? 'text-green-400' : 'text-gray-400'
                                }`}>
                                {comment.isVisible ? 'VISIBLE' : 'HIDDEN'}
                              </span>
                              <ToggleSwitch
                                checked={comment.isVisible || false}
                                onChange={() => toggleCommentVisibility(comment.id, !comment.isVisible)}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-400 text-sm text-center py-8">
                          No comments yet
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Controls */}
          <div className="space-y-8">
            {/* Countdown Clocks */}
            <div className='bg-[#1c1c1c] p-6'>
              <h2 className="text-white text-xl font-bold mb-6 text-center">Countdown Clocks</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white">Primary Countdown</span>
                  <ToggleSwitch
                    checked={controlState.primaryCountdownVisible}
                    onChange={() => handleToggle('primaryCountdownVisible')}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCountdownStateChange('DEFAULT');
                    }}
                    className={`px-4 py-1 text-sm font-medium border-2 border-gray-600 ${controlState.countdownState === 'DEFAULT'
                        ? 'bg-gray-600 text-white'
                        : 'bg-black text-white hover:bg-gray-800'
                      }`}
                  >
                    DEFAULT
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCountdownStateChange('SCRUB');
                    }}
                    className={`px-4 py-1 text-sm font-medium border-2 border-red-600 ${controlState.countdownState === 'SCRUB'
                        ? 'bg-red-600 text-white'
                        : 'bg-black text-white hover:bg-gray-800'
                      }`}
                  >
                    SCRUB
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCountdownStateChange('HOLD');
                    }}
                    className={`px-4 py-1 text-sm font-medium border-2 border-orange-600 ${controlState.countdownState === 'HOLD'
                        ? 'bg-orange-600 text-white'
                        : 'bg-black text-white hover:bg-gray-800'
                      }`}
                  >
                    HOLD
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCountdownStateChange('AWAITING_UPDATE');
                    }}
                    className={`px-4 py-1 text-sm font-medium border-2 border-yellow-600 ${controlState.countdownState === 'AWAITING_UPDATE'
                        ? 'bg-yellow-600 text-black'
                        : 'bg-black text-white hover:bg-gray-800'
                      }`}
                  >
                    AWAITING UPDATE
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCountdownStateChange('GO');
                    }}
                    className={`px-4 py-1 text-sm font-medium border-2 border-green-600 ${controlState.countdownState === 'GO'
                        ? 'bg-green-600 text-white'
                        : 'bg-black text-white hover:bg-gray-800'
                      }`}
                  >
                    GO
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Launch Window</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm">Close</span>
                    <ToggleSwitch
                      checked={controlState.launchWindowVisible}
                      onChange={() => handleToggle('launchWindowVisible')}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Secondary Clock Option</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm">Close Clock Window</span>
                    <ToggleSwitch
                      checked={controlState.secondaryClockClose}
                      onChange={() => handleToggle('secondaryClockClose')}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Clock Option</span>
                  <div className="flex items-center gap-8">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm">Day</span>
                      <ToggleSwitch
                        checked={controlState.showDays}
                        onChange={() => handleToggle('showDays')}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm">Hr</span>
                      <ToggleSwitch
                        checked={controlState.showHours}
                        onChange={() => handleToggle('showHours')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Updates */}
            <div className='bg-[#1c1c1c] p-6'>
              <h2 className="text-white text-xl font-bold mb-6 text-center">Status Updates</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white">Header</span>
                  <ToggleSwitch
                    checked={controlState.headerVisible}
                    onChange={() => handleToggle('headerVisible')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Subheader</span>
                  <ToggleSwitch
                    checked={controlState.subheaderVisible}
                    onChange={() => handleToggle('subheaderVisible')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Header 3</span>
                  <ToggleSwitch
                    checked={controlState.header3Visible}
                    onChange={() => handleToggle('header3Visible')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Update Banner</span>
                  <ToggleSwitch
                    checked={controlState.updateBannerVisible}
                    onChange={() => handleToggle('updateBannerVisible')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Member Scroll</span>
                  <ToggleSwitch
                    checked={controlState.memberScrollVisible}
                    onChange={() => handleToggle('memberScrollVisible')}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-white">Member Bar</span>
                  <ToggleSwitch
                    checked={controlState.memberBarVisible}
                    onChange={() => handleToggle('memberBarVisible')}
                  />
                </div>
              </div>
            </div>

            {/* Headlines */}
            <div className='bg-[#1c1c1c] p-6'>
              <h2 className="text-white text-xl font-bold mb-6 text-center">Headlines</h2>

              <div className="space-y-4">
                {/* Status Type Selection */}
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Status Type</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setUpdateType(null)}
                      className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${
                        updateType === null
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-transparent text-white border-red-600 hover:bg-red-600 hover:bg-opacity-20'
                      }`}
                    >
                      No Status Title
                    </button>
                    <button
                      type="button"
                      onClick={() => setUpdateType('BREAKING_NEWS')}
                      className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${
                        updateType === 'BREAKING_NEWS'
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-transparent text-white border-red-600 hover:bg-red-600 hover:bg-opacity-20'
                      }`}
                    >
                      Breaking News
                    </button>
                    <button
                      type="button"
                      onClick={() => setUpdateType('DEVELOPING')}
                      className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${
                        updateType === 'DEVELOPING'
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-transparent text-white border-red-600 hover:bg-red-600 hover:bg-opacity-20'
                      }`}
                    >
                      Developing
                    </button>
                    <button
                      type="button"
                      onClick={() => setUpdateType('STATUS_UPDATE')}
                      className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${
                        updateType === 'STATUS_UPDATE'
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-transparent text-white border-red-600 hover:bg-red-600 hover:bg-opacity-20'
                      }`}
                    >
                      Status Update
                    </button>
                    <button
                      type="button"
                      onClick={() => setUpdateType('UP_NEXT')}
                      className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${
                        updateType === 'UP_NEXT'
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-transparent text-white border-red-600 hover:bg-red-600 hover:bg-opacity-20'
                      }`}
                    >
                      Up Next
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Headline 1</label>
                  <input
                    type="text"
                    value={headlines.headline1}
                    onChange={(e) => handleHeadlineChange('headline1', e.target.value)}
                    className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
                    placeholder="Type Text Here"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Sub Headline</label>
                  <input
                    type="text"
                    value={headlines.subHeadline}
                    onChange={(e) => handleHeadlineChange('subHeadline', e.target.value)}
                    className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
                    placeholder="Type Text Here"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Headline 3</label>
                  <input
                    type="text"
                    value={headlines.headline3}
                    onChange={(e) => handleHeadlineChange('headline3', e.target.value)}
                    className="w-full bg-black border border-white text-white px-4 py-2 focus:outline-none focus:border-red-500"
                    placeholder="Type Text Here"
                  />
                </div>
                <button
                  onClick={saveHeadlines}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition-colors"
                >
                  Save Headlines
                </button>
              </div>
            </div>

            {/* Pop Ups */}
            <div className='bg-[#1c1c1c] p-6'>
              <h2 className="text-white text-xl font-bold mb-6 text-center">Pop Ups</h2>

              {/* Category Buttons */}
              <div className="flex gap-2 flex-wrap mb-6">
                {/* Social Media Category */}
                {getElementsByType('SOCIAL').length > 0 && (
                  <button
                    onClick={() => {
                      // Trigger all social media popups
                      getElementsByType('SOCIAL').forEach(social => {
                        triggerPopup('SOCIAL', social.name);
                      });
                    }}
                    className="bg-black border border-white text-white px-3 py-1 text-sm hover:bg-gray-800 transition-colors"
                  >
                    Social Media
                  </button>
                )}

                {/* Website Category */}
                {getElementsByType('HOST').length > 0 && (
                  <button
                    onClick={() => {
                      // Trigger all host popups
                      getElementsByType('HOST').forEach(host => {
                        triggerPopup('HOST', host.name);
                      });
                    }}
                    className="bg-black border border-white text-white px-3 py-1 text-sm hover:bg-gray-800 transition-colors"
                  >
                    Website
                  </button>
                )}

                {/* Shop Category */}
                {getElementsByType('SHOP').length > 0 && (
                  <button
                    onClick={() => {
                      // Trigger all shop popups
                      getElementsByType('SHOP').forEach(shop => {
                        triggerPopup('SHOP', shop.name);
                      });
                    }}
                    className="bg-black border border-white text-white px-3 py-1 text-sm hover:bg-gray-800 transition-colors"
                  >
                    Shop
                  </button>
                )}
              </div>

              {/* Individual Popup Elements */}
              <div className="space-y-4">

                {/* Hosts */}
                <div>
                  <div className="text-white text-sm mb-2">Hosts</div>
                  <div className="border-b border-white mb-3"></div>
                  <div className="flex gap-2 flex-wrap">
                    {getElementsByType('HOST').length > 0 ? (
                      getElementsByType('HOST').map((host) => {
                        const isSelected = selectedPopups.some(selected => selected.id === host.id)
                        const isActive = host.is_active
                        return (
                          <button
                            key={host.id}
                            onClick={() => togglePopupSelection(host)}
                            className={`px-3 py-1 text-sm transition-colors ${isSelected
                                ? 'bg-red-600 text-white'
                                : isActive
                                  ? 'bg-black border border-white text-white hover:bg-gray-800'
                                  : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700'
                              }`}
                            title={isActive ? 'Active popup' : 'Inactive popup'}
                          >
                            {host.name} {!isActive && '(Inactive)'}
                          </button>
                        )
                      })
                    ) : (
                      <div className="text-gray-400 text-sm">No hosts configured</div>
                    )}
                  </div>
                </div>

                {/* Producers */}
                <div>
                  <div className="text-white text-sm mb-2">Producers</div>
                  <div className="border-b border-white mb-3"></div>
                  <div className="flex gap-2 flex-wrap">
                    {getElementsByType('PROMO').length > 0 ? (
                      getElementsByType('PROMO').map((producer) => {
                        const isSelected = selectedPopups.some(selected => selected.id === producer.id)
                        const isActive = producer.is_active
                        return (
                          <button
                            key={producer.id}
                            onClick={() => togglePopupSelection(producer)}
                            className={`px-3 py-1 text-sm transition-colors ${isSelected
                                ? 'bg-red-600 text-white'
                                : isActive
                                  ? 'bg-black border border-white text-white hover:bg-gray-800'
                                  : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700'
                              }`}
                            title={isActive ? 'Active popup' : 'Inactive popup'}
                          >
                            {producer.name} {!isActive && '(Inactive)'}
                          </button>
                        )
                      })
                    ) : (
                      <div className="text-gray-400 text-sm">No producers configured</div>
                    )}
                  </div>
                </div>

                {/* Shop Items */}
                <div>
                  <div className="text-white text-sm mb-2">Shop Items</div>
                  <div className="border-b border-white mb-3"></div>
                  <div className="flex gap-2 flex-wrap">
                    {getElementsByType('SHOP').length > 0 ? (
                      getElementsByType('SHOP').map((shop) => {
                        const isSelected = selectedPopups.some(selected => selected.id === shop.id)
                        const isActive = shop.is_active
                        return (
                          <button
                            key={shop.id}
                            onClick={() => togglePopupSelection(shop)}
                            className={`px-3 py-1 text-sm transition-colors ${isSelected
                                ? 'bg-red-600 text-white'
                                : isActive
                                  ? 'bg-black border border-white text-white hover:bg-gray-800'
                                  : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700'
                              }`}
                            title={isActive ? 'Active popup' : 'Inactive popup'}
                          >
                            {shop.name} {!isActive && '(Inactive)'}
                          </button>
                        )
                      })
                    ) : (
                      <div className="text-gray-400 text-sm">No shop items configured</div>
                    )}
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <div className="text-white text-sm mb-2">Social Media</div>
                  <div className="border-b border-white mb-3"></div>
                  <div className="flex gap-2 flex-wrap">
                    {getElementsByType('SOCIAL').length > 0 ? (
                      getElementsByType('SOCIAL').map((social) => {
                        const isSelected = selectedPopups.some(selected => selected.id === social.id)
                        const isActive = social.is_active
                        return (
                          <button
                            key={social.id}
                            onClick={() => togglePopupSelection(social)}
                            className={`px-3 py-1 text-sm transition-colors ${isSelected
                                ? 'bg-red-600 text-white'
                                : isActive
                                  ? 'bg-black border border-white text-white hover:bg-gray-800'
                                  : 'bg-gray-800 border border-gray-600 text-gray-400 hover:bg-gray-700'
                              }`}
                            title={isActive ? 'Active popup' : 'Inactive popup'}
                          >
                            {social.name} {!isActive && '(Inactive)'}
                          </button>
                        )
                      })
                    ) : (
                      <div className="text-gray-400 text-sm">No social media configured</div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Mode Password Modal */}
      {showEmergencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-[#1c1c1c] border-2 border-red-600 p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-white text-xl font-bold mb-4 text-center">EMERGENCY MODE</h2>
            <p className="text-white text-sm mb-4 text-center">
              Enter your login password to activate emergency mode. This will hide all elements except those touching edges and change all colors to dark grey.
            </p>
            <input
              type="password"
              value={emergencyPassword}
              onChange={(e) => setEmergencyPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleEmergencyActivation()
                }
              }}
              placeholder="Enter emergency password"
              className="w-full bg-black border border-white text-white px-4 py-2 mb-4 focus:outline-none focus:border-red-500"
              autoFocus
            />
            <div className="flex gap-4">
              <button
                onClick={handleEmergencyActivation}
                className="flex-1 bg-red-600 text-white px-4 py-2 font-bold hover:bg-red-700 transition-colors"
              >
                ACTIVATE
              </button>
              <button
                onClick={() => {
                  setShowEmergencyModal(false)
                  setEmergencyPassword('')
                }}
                className="flex-1 bg-gray-700 text-white px-4 py-2 font-bold hover:bg-gray-600 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OverlayControl